/**
 * ✅ Migrated to authStore_v1 - 2026-01-07
 * 
 * Axios Instance Configuration
 * 
 * Centralized axios instance with:
 * - Cookie-based authentication
 * - Token refresh logic (clears cookie + redirects to login)
 * - Request/Response interceptors
 * - Error handling
 */

import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';

/**
 * Extended Axios Request Config
 * 
 * Custom properties for controlling request behavior:
 * - skipAuth: Don't add Authorization header (for public endpoints)
 * - _retry: Internal flag to prevent infinite retry loops
 */
type AxiosRequestConfigWithRetry = {
  skipAuth?: boolean;  // Skip auth token for public endpoints
  _retry?: boolean;    // Internal: Retry flag
} & AxiosRequestConfig;

// Extend InternalAxiosRequestConfig with our custom properties
interface InternalAxiosRequestConfigExtended extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
  _retry?: boolean;
}

const apiBaseUrl = env.API_URL;

/** Read a cookie by name (used for refresh token outside React) */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Token management utilities - Read from authStore_v1
export function getAuthToken(): string | null {
  // Read token from store (set by App.tsx from cookie)
  const { useAuthStoreV1 } = require('@/stores/authStore_v1');
  return useAuthStoreV1.getState().token || getCookie('auth_token');
}

/** Read refresh token from cookie (for use in interceptor without React) */
export function getRefreshToken(): string | null {
  return getCookie('refresh_token');
}

/** Set both auth and refresh token cookies (for use after refreshJwt, outside React) */
function setAuthTokensCookie(jwtToken: string, refreshToken: string, jwtMaxAgeSeconds: number): void {
  const baseOpts = 'path=/; secure; samesite=strict';
  document.cookie = `auth_token=${encodeURIComponent(jwtToken)}; ${baseOpts}; max-age=${jwtMaxAgeSeconds}`;
  const refreshMaxAge = 30 * 24 * 60 * 60; // 30 days
  document.cookie = `refresh_token=${encodeURIComponent(refreshToken)}; ${baseOpts}; max-age=${refreshMaxAge}`;
}

export function clearAuthToken(): void {
  // Clear auth and refresh cookies (since we can't use react-cookie hooks outside React)
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = `auth_token=; ${expire}`;
  document.cookie = `refresh_token=; ${expire}`;

}

/**
 * Token refresh: call refreshJwt, persist new tokens, return new JWT or null.
 * Used by 401 error interceptor. Dynamic import to avoid circular dependency with auth.graphql.
 *
 * Concurrent callers share a single in-flight promise — the refresh token rotates on
 * each successful refresh, so parallel refreshes with the same (now stale) token
 * would race and the losers would log the user out.
 */
let inFlightRefresh: Promise<string | null> | null = null;

async function refreshAuthToken(): Promise<string | null> {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  inFlightRefresh = (async () => {
    try {
      const { refreshJwt: refreshJwtApi } = await import('@/services/salesforce/auth.graphql');
      const data = await refreshJwtApi(refreshToken);
      const { jwtToken: newJwt, refreshToken: newRefresh } = data.refreshJwt;

      // Persist both tokens (cookies + store)
      const decoded = (() => {
        try {
          const base64Url = newJwt.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const json = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          return JSON.parse(json) as { exp?: number };
        } catch {
          return null;
        }
      })();
      const now = Math.floor(Date.now() / 1000);
      const jwtMaxAge = decoded?.exp ? Math.max(1, decoded.exp - now) : 7 * 24 * 60 * 60;
      setAuthTokensCookie(newJwt, newRefresh, jwtMaxAge);

      const { useAuthStoreV1 } = await import('@/stores/authStore_v1');
      useAuthStoreV1.getState().setToken(newJwt);

      return newJwt;
    } catch {
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/**
 * Create axios instance with base configuration
 */
export const axiosInstance: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  // 120s — agent/metadata graph endpoints can legitimately take 30-90s
  // under load, especially when backend forwards to agentic-platform and DB
  // graph queries fan out. Spurious 30s timeouts surfaced as "Uncaught
  // runtime error" toasts even though the request would have completed.
  timeout: 120000,
  withCredentials: false, // Explicitly disable credentials to avoid CORS preflight issues
});

/**
 * Request Interceptor
 * 
 * Automatically adds Authorization header with JWT token (unless skipAuth is set)
 * Supports baseURL override per request
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfigExtended) => {
    const token = config.skipAuth === true ? null : getAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

/** GraphQL error with optional auth_error (e.g. salesforce_token_expired) */
type GraphQLErrorItem = { message?: string; code?: string; auth_error?: string };

function clearSessionAndRedirectToLogin(): void {
  clearAuthToken();
  import('@/stores/authStore_v1').then(({ useAuthStoreV1 }) => {
    useAuthStoreV1.getState().clearAll();
  });
  import('react-hot-toast').then(({ default: toast }) => {
    toast.error('Session timed out, please login again.', { duration: 4000 });
  });
  setTimeout(() => {
    window.location.href = '/auth/login';
  }, 1500);
}

/** GraphQL 200 + errors: redirect to login and reject with a specific Error message */
function rejectGraphQLAuthRedirect(logMessage: string, rejectReason: string): Promise<never> {
  console.warn(logMessage);
  clearSessionAndRedirectToLogin();
  return Promise.reject(new Error(rejectReason));
}


const FRIENDLY_MESSAGE_BY_STATUS: Readonly<Record<number, string>> = {
  401: 'Your session expired — please sign in again',
  403: "Sorry, you don't have access to this",
  404: "Sorry, we couldn't find that",
  408: 'Request timed out — please try again',
  429: 'Too many requests — please slow down',
};

function friendlyMessageForAxiosError(error: AxiosError): string {
  const status = error.response?.status;
  // `error`/`errors` cover Phoenix-style JSON error bodies (e.g. `%{error: reason}` or a
  // changeset's `%{errors: %{field: [msg]}}`), in addition to the `detail`/`message` shapes.
  const data: { detail?: unknown; message?: unknown; error?: unknown; errors?: unknown } | undefined =
    error.response?.data as
      | { detail?: unknown; message?: unknown; error?: unknown; errors?: unknown }
      | undefined;
  const backendDetail =
    (data && typeof data === 'object' && (data.detail || data.message || data.error)) || null;
  if (backendDetail && typeof backendDetail === 'string') {
    return backendDetail;
  }
  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors as Record<string, unknown>)[0];
    const firstMessage = Array.isArray(firstError) ? firstError[0] : firstError;
    if (typeof firstMessage === 'string') return firstMessage;
  }
  // Same evaluation order as the original if/else chain
  if (status === 401) return FRIENDLY_MESSAGE_BY_STATUS[401];
  if (status === 403) return FRIENDLY_MESSAGE_BY_STATUS[403];
  if (status === 404) return FRIENDLY_MESSAGE_BY_STATUS[404];
  if (status === 408 || error.code === 'ECONNABORTED') return FRIENDLY_MESSAGE_BY_STATUS[408];
  if (status === 429) return FRIENDLY_MESSAGE_BY_STATUS[429];
  if (status && status >= 500) return "The server isn't responding right now — please try again";
  if (!status) return "Sorry, couldn't reach the server";
  return "Sorry, that didn't work — please try again";
}

/**
 * Response Interceptor
 * Handles salesforce_token_expired (redirect to login), other GraphQL errors (reject), and unwraps backend response
 */
axiosInstance.interceptors.response.use(
  async (response) => {
    // Check for GraphQL errors in response (200 with errors in body)
    if (response.data && typeof response.data === 'object' && 'errors' in response.data) {
      const errors = (response.data.errors as Array<GraphQLErrorItem>) ?? [];

      // Salesforce-specific errors: JWT refresh won't help, redirect directly
      const isSalesforceExpired = errors.some(
        (e) =>
          e.auth_error === 'salesforce_token_expired' ||
          (typeof e.message === 'string' && e.message.includes('refresh_token_expired'))
      );
      if (isSalesforceExpired) {
        return rejectGraphQLAuthRedirect(
          '⚠️ Salesforce session expired. Redirecting to login...',
          'Salesforce session expired'
        );
      }

      // JWT auth errors (Unauthenticated / Unauthorized): try token refresh + retry first
      const isJwtAuthError = errors.some(
        (e) =>
          e.message === 'Unauthenticated' ||
          e.code === 'UNAUTHENTICATED' ||
          e.message === 'Unauthorized'
      );
      if (isJwtAuthError) {
        const originalRequest = response.config as InternalAxiosRequestConfigExtended;
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = getRefreshToken();
          if (refreshToken) {
            const newToken = await refreshAuthToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            }
          }
        }
        return rejectGraphQLAuthRedirect(
          '⚠️ Unauthenticated. Redirecting to login...',
          'Unauthenticated'
        );
      }
    }

    // Backend response format: { data, message, status, statusCode }
    // Unwrap the data for easier consumption
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return {
        ...response,
        data: response.data.data, // Unwrap the actual data
        _originalResponse: response.data, // Keep original for error handling
      };
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfigExtended;

    // Handle 401 Unauthorized: try refresh, then retry once
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearSessionAndRedirectToLogin();
        return Promise.reject(error);
      }

      const newToken = await refreshAuthToken();

      if (newToken && originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      }

      // Refresh failed: clear and redirect
      clearSessionAndRedirectToLogin();
      return Promise.reject(error);
    } else if (error.response?.status === 401 && originalRequest?._retry) {
      // Retried after token refresh but still got 401 — session is invalid
      clearSessionAndRedirectToLogin();
      return Promise.reject(error);
    }
    
    // Log error in development (skip for callers that opt out via { silent: true })
    if (env.NCODER_ENV === 'local' && !(error.config as any)?.silent) {
      console.error('❌ API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
    }
    
    // Attach formatted error response for React Query error handler
    if (error.response?.data) {
      (error as any).response = {
        ...error.response,
        data: error.response.data, // Backend format: { message, status, statusCode }
      };
    }

    // Rewrite `error.message` to a user-friendly string so every existing
    // `toast.error(err?.message || "...")` callsite shows something readable
    // instead of "Request failed with status code 400". The original axios
    // message is preserved on `error.rawMessage` for debugging.
    try {
      (error as any).rawMessage = error.message;
      error.message = friendlyMessageForAxiosError(error);
    } catch {
      /* never let error formatting itself throw */
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to clear auth token (for use after logout)
 * Note: Actual cookie clearing is handled by useAuth hook
 */
export function clearApiAuthToken(): void {
  clearAuthToken();
}

/**
 * Create a custom axios instance with optional baseURL override
 * 
 * @param baseURL - Optional custom base URL (defaults to env.API_URL)
 * @param skipAuth - If true, all requests skip auth by default
 * @returns Configured axios instance
 * 
 * @example
 * // Create an instance for a different API
 * const customApi = createAxiosInstance('https://api.example.com');
 * 
 * // Create a public API instance (no auth by default)
 * const publicApi = createAxiosInstance(undefined, true);
 */
export function createAxiosInstance(
  baseURL?: string,
  skipAuth?: boolean
): AxiosInstance {
  const instance = axios.create({
    baseURL: baseURL || apiBaseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    // Match the default axiosInstance — see note above. Agent/metadata
    // graph endpoints can legitimately take 30-90s.
    timeout: 120000,
    withCredentials: false,
  });

  // Add request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfigExtended) => {
      const token = (skipAuth || config.skipAuth === true) ? null : getAuthToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add response interceptor with auth handling + redirect
  instance.interceptors.response.use(
    async (response) => {
      // Some proxied endpoints return HTTP 200 with body { "error": "Unauthorized" }
      // instead of a 401 status — treat that the same as a 401 and try refresh + retry.
      const body = response.data as { error?: string } | null;
      if (body && typeof body === 'object' && body.error === 'Unauthorized') {
        const originalRequest = response.config as InternalAxiosRequestConfigExtended;
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = await refreshAuthToken();
          if (newToken && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          }
        }
        clearSessionAndRedirectToLogin();
        return Promise.reject(new Error('Unauthorized'));
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfigExtended;

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const newToken = await refreshAuthToken();

        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        }

        clearSessionAndRedirectToLogin();
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && originalRequest?._retry) {
        clearSessionAndRedirectToLogin();
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

// Export type for easier usage
export type { AxiosRequestConfigWithRetry };

/** Best-effort JWT refresh — dev-agent errorHandler & SSE ticket recovery. */
export async function attemptAuthRecovery(): Promise<boolean> {
  const token = await refreshAuthToken();
  return !!token;
}

/** Clear session and redirect to login (e.g. SSE `unauthorized` with no ticket refresh). */
export function clearFrontendSessionAndRedirectToLogin(): void {
  clearSessionAndRedirectToLogin();
}

export default axiosInstance;
